import { formatProfitPct, resolveProfitProfile, reversalNeedsBoth } from '../../utils/profitProtect';

const RSI_LIMIT = 65;

function numeric(value) {
    const number = Number(value);
    return Number.isFinite(number) ? number : null;
}

function clamp(value, min, max) {
    return Math.min(max, Math.max(min, value));
}

function pairFill(current, target, previousCurrent, previousTarget) {
    if (current == null || target == null)
        return null;
    const values = [current, target, previousCurrent, previousTarget].filter(value => value != null);
    const lo = Math.min(...values);
    const hi = Math.max(...values);
    const pad = Math.max((hi - lo) * 0.25, Math.abs(target || current) * 0.04, 1e-10);
    const rangeLo = lo - pad;
    const rangeHi = hi + pad;
    const span = rangeHi - rangeLo || 1;
    return {
        fill: clamp(((current - rangeLo) / span) * 100, 2, 98),
        mark: clamp(((target - rangeLo) / span) * 100, 4, 96),
        ok: current > target
    };
}

function buyStory(indicators) {
    const macd = numeric(indicators?.macd);
    const signal = numeric(indicators?.macdSignal);
    const rsi = numeric(indicators?.rsi);
    const ema20 = numeric(indicators?.ema20);
    const ema50 = numeric(indicators?.ema50);
    const macdPair = pairFill(macd, signal, numeric(indicators?.macdPrevious), numeric(indicators?.macdSignalPrevious));
    const emaPair = pairFill(ema20, ema50);
    const rsiOk = rsi != null && rsi < RSI_LIMIT;
    const macdOk = Boolean(macdPair?.ok);
    const emaOk = Boolean(emaPair?.ok);
    const gates = [
        macdPair && {
            key: 'macd',
            label: '1. MACD',
            fill: macdPair.fill,
            mark: macdPair.mark,
            markLabel: 'Signal',
            value: macdOk ? 'Cruzou' : 'Abaixo',
            ok: macdOk,
            hint: macdOk
                ? 'MACD já está acima do signal.'
                : 'A barra anda até a marca. Quando encostar, cruzou.'
        },
        rsi != null && {
            key: 'rsi',
            label: '2. RSI',
            fill: clamp(rsi, 2, 98),
            mark: RSI_LIMIT,
            markLabel: '< 65',
            value: rsi.toFixed(1),
            ok: rsiOk,
            over: rsi >= RSI_LIMIT,
            hint: rsiOk
                ? `RSI ${rsi.toFixed(1)} está abaixo de 65.`
                : `RSI ${rsi.toFixed(1)} passou de 65. A compra espera ele cair.`
        },
        emaPair && {
            key: 'ema',
            label: '3. EMA',
            fill: emaPair.fill,
            mark: emaPair.mark,
            markLabel: 'EMA 50',
            value: emaOk ? 'Alta' : 'Abaixo',
            ok: emaOk,
            hint: emaOk
                ? 'EMA 20 já está acima da EMA 50.'
                : 'A EMA 20 precisa ficar acima da EMA 50.'
        }
    ].filter(Boolean);

    if (!gates.length)
        return null;

    const missing = [];
    if (macdPair && !macdOk) missing.push('MACD cruzar o signal');
    if (rsi != null && !rsiOk) missing.push(`RSI cair abaixo de 65 (agora ${rsi.toFixed(1)})`);
    if (emaPair && !emaOk) missing.push('EMA 20 ficar acima da EMA 50');

    return {
        mode: 'buy',
        ready: macdOk && rsiOk && emaOk,
        title: macdOk && rsiOk && emaOk
            ? 'Pode comprar'
            : missing.length === 1
                ? `Não compra ainda: falta ${missing[0]}`
                : 'Não compra ainda',
        caption: macdOk && rsiOk && emaOk
            ? 'As três regras fecharam. A compra deve sair sozinha.'
            : `Precisa das três: MACD cruzado, RSI abaixo de 65 e EMA de alta.${missing.length ? ` Falta: ${missing.join('; ')}.` : ''}`,
        gates
    };
}

function sellStory(monitor, indicators) {
    const profile = resolveProfitProfile(monitor);
    const minProfit = profile.armProfit;
    const perc = numeric(monitor?.profitPerc);
    const stop = numeric(monitor?.stopLoss) || 0;
    const macd = numeric(indicators?.macd);
    const signal = numeric(indicators?.macdSignal);
    const ema20 = numeric(indicators?.ema20);
    const ema50 = numeric(indicators?.ema50);
    const macdDown = macd != null && signal != null && macd < signal;
    const emaDown = ema20 != null && ema50 != null && ema20 < ema50;
    const locked = Boolean(monitor?.trailArmed);
    const armed = locked || (perc != null && perc >= minProfit);
    const floorPerc = numeric(monitor?.trailFloorPerc);
    const lockAt = floorPerc != null ? floorPerc : minProfit;
    const gaveBack = armed && perc != null && perc < lockAt;
    const reversalReady = perc != null && perc >= profile.reversalMinProfit;
    const reversalHit = reversalNeedsBoth(profile)
        ? (macdDown && emaDown)
        : (macdDown || emaDown);
    const reversalSells = armed && reversalReady && reversalHit;
    const lo = stop > 0 ? -stop : Math.min(0, perc || 0);
    const hi = Math.max(minProfit * 1.5, perc || 0, lockAt, minProfit, profile.reversalMinProfit);
    const span = hi - lo || 1;
    const gates = [];
    const armLabel = `${formatProfitPct(minProfit)}%`;
    const both = reversalNeedsBoth(profile);

    if (perc != null) {
        gates.push({
            key: 'profit',
            label: 'Lucro',
            fill: clamp(((perc - lo) / span) * 100, 2, 98),
            mark: clamp(((lockAt - lo) / span) * 100, 4, 96),
            markLabel: `${lockAt.toFixed(1).replace('.', ',')}%`,
            value: `${perc > 0 ? '+' : ''}${perc.toFixed(2)}%`,
            ok: armed && !gaveBack,
            over: gaveBack,
            hint: gaveBack
                ? `Recuou até o piso móvel (${lockAt.toFixed(1).replace('.', ',')}%). A venda deve sair.`
                : armed
                    ? `Piso móvel em ${lockAt.toFixed(1).replace('.', ',')}% (${formatProfitPct(profile.giveback)}% abaixo do pico). Se recuar até aí, vende.`
                    : `A barra anda até ${armLabel}. Aí o piso começa a acompanhar o topo.`
        });
    }

    const macdPair = pairFill(macd, signal, numeric(indicators?.macdPrevious), numeric(indicators?.macdSignalPrevious));
    if (macdPair) {
        const macdWouldSell = armed && reversalReady && (both ? (macdDown && emaDown) : macdDown);
        gates.push({
            key: 'macd',
            label: 'MACD',
            fill: macdPair.fill,
            mark: macdPair.mark,
            markLabel: 'Signal',
            value: macdDown ? 'Reverteu' : 'Ainda acima',
            ok: !macdDown,
            over: macdWouldSell,
            hint: macdDown
                ? (armed
                    ? (both && !emaDown
                        ? 'MACD reverteu, mas a EMA ainda está de alta. Nesta config os dois precisam reverter para vender.'
                        : (!reversalReady
                            ? `MACD reverteu, mas o lucro ainda não chegou em ${formatProfitPct(profile.reversalMinProfit)}%. Só o piso móvel vende até lá.`
                            : 'MACD reverteu com o piso armado. A venda pode sair.'))
                    : `MACD reverteu, mas o lucro ainda não chegou em ${armLabel}.`)
                : `MACD segue acima. Enquanto não reverter e o preço ficar acima de ${armLabel}, deixa correr.`
        });
    }

    const emaPair = pairFill(ema20, ema50);
    if (emaPair) {
        const emaWouldSell = armed && reversalReady && (both ? (macdDown && emaDown) : emaDown);
        gates.push({
            key: 'ema',
            label: 'EMA',
            fill: emaPair.fill,
            mark: emaPair.mark,
            markLabel: 'EMA 50',
            value: emaDown ? 'Reverteu' : 'Ainda alta',
            ok: !emaDown,
            over: emaWouldSell,
            hint: emaDown
                ? (armed
                    ? (both && !macdDown
                        ? 'EMA reverteu, mas o MACD ainda está de alta. Nesta config os dois precisam reverter para vender.'
                        : (!reversalReady
                            ? `EMA reverteu, mas o lucro ainda não chegou em ${formatProfitPct(profile.reversalMinProfit)}%. Só o piso móvel vende até lá.`
                            : 'EMA reverteu com o piso armado. A venda pode sair.'))
                    : `EMA reverteu, mas o lucro ainda não chegou em ${armLabel}.`)
                : 'EMA 20 segue acima da 50. Tendência de alta continua.'
        });
    }

    if (!gates.length)
        return null;

    const shouldSell = gaveBack || reversalSells;
    return {
        mode: 'sell',
        ready: shouldSell,
        title: shouldSell
            ? 'Pode vender'
            : armed
                ? 'Piso móvel — acompanha o pico'
                : `Em posição — aproximando de ${armLabel}`,
        caption: shouldSell
            ? (gaveBack
                ? `O preço recuou ${formatProfitPct(profile.giveback)}% do pico (piso móvel). A venda deve sair sozinha.`
                : 'Piso armado e a tendência reverteu. A venda deve sair sozinha.')
            : armed
                ? (both
                    ? `Passou de ${armLabel}. O piso sobe atrás do topo. MACD e EMA só vendem juntos depois de ${formatProfitPct(profile.reversalMinProfit)}%.`
                    : `Passou de ${armLabel}. O piso sobe atrás do topo (sempre ${formatProfitPct(profile.giveback)}% abaixo do pico). Se MACD ou EMA reverterem, também vende.`)
                : `Ainda abaixo de ${armLabel}. Só vale o stop de prejuízo. O piso móvel começa quando a barra encostar na marca.`,
        gates
    };
}

function GateBar({ gate }) {
    const state = gate.over ? 'over' : gate.ok ? 'ok' : 'wait';
    return (
        <div className={`profit-gate profit-gate-${state}`}>
            <div className="profit-gate-head">
                <strong>{gate.label}</strong>
                <span>{gate.value}</span>
            </div>
            <div className="profit-gate-track" role="progressbar" aria-valuenow={Math.round(gate.fill)}>
                <div className="profit-gate-fill" style={{ width: `${gate.fill}%` }} />
                <div className="profit-gate-mark" style={{ left: `${gate.mark}%` }}>
                    <small>{gate.markLabel}</small>
                </div>
                <div className="profit-gate-tip" style={{ left: `${gate.fill}%` }} />
            </div>
            <p className="profit-gate-hint">{gate.hint}</p>
        </div>
    );
}

function ProfitGoalBar({ analysis, monitor, compact = false }) {
    const inPosition = monitor?.status === 'IN_POSITION';
    const story = inPosition
        ? sellStory(monitor, analysis?.indicators)
        : buyStory(analysis?.indicators);

    if (!story)
        return compact
            ? <p className="profit-empty">Sem indicadores neste par no momento.</p>
            : null;

    return (
        <div className={`profit-story profit-story-${story.mode} ${story.ready ? 'ready' : ''} ${compact ? 'compact' : ''}`}>
            <div className="profit-story-banner">
                <span className="profit-story-pulse" />
                <div>
                    <strong>{story.title}</strong>
                    <p>{story.caption}</p>
                </div>
            </div>
            <div className="profit-story-gates">
                {story.gates.map(gate => (
                    <GateBar key={gate.key} gate={gate} />
                ))}
            </div>
        </div>
    );
}

export default ProfitGoalBar;
